import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { scheduledPatientInformationAtom } from '../schedule-preview/state';
import { AppointmentLocation, RecurringAppointmentGrouping } from '../schedule-preview/types';
import { EditAppointmentDrawerBodyWrapper as EditAppointmentDrawerBody } from './EditAppointmentDrawerBody';
import { EditAppointmentDrawerFooterWrapper as EditAppointmentDrawerFooter } from './EditAppointmentDrawerFooter';
import { EditAppointmentDrawerHeaderWrapper as EditAppointmentDrawerHeader } from './EditAppointmentDrawerHeader';
import { GetSchedulingAlertsQueryData, ScheduleEventType, useGetSchedulingAlerts } from './useGetSchedulingAlerts';
import { getTimeInputValidationMessage } from './utils';

export type EditAppointmentFormValues = {
  appointmentLocation?: AppointmentLocation;
  startDate: Date;
  endDate: Date;
  isRecurring?: boolean;
  weeksBetween?: number;
  numRecurrences?: number;
  indefinite?: boolean;
  recurringAppointmentGrouping?: RecurringAppointmentGrouping;
  appointmentId?: string;
  alertsData?: GetSchedulingAlertsQueryData;
};

export function EditAppointmentDrawer({
  formValues,
  onUpdateValues,
  timeInputErrorMessage,
  alertsData,
}: {
  formValues: EditAppointmentFormValues;
  onUpdateValues: (values: Partial<EditAppointmentFormValues>) => void;
  timeInputErrorMessage?: string;
  alertsData?: GetSchedulingAlertsQueryData;
}) {
  const isSaveDisabled =
    !!timeInputErrorMessage || !!alertsData?.schedulingAlerts?.some((alert) => alert?.alertLevel === 'error');
  return (
    <>
      <EditAppointmentDrawerHeader />
      <EditAppointmentDrawerBody
        formValues={formValues}
        onUpdateValues={onUpdateValues}
        timeInputErrorMessage={timeInputErrorMessage}
        alertsData={alertsData}
      />
      <EditAppointmentDrawerFooter formValues={formValues} isSaveDisabled={isSaveDisabled} />
    </>
  );
}

export function EditAppointmentDrawerWrapper() {
  const appointmentData = useAtomValue(scheduledPatientInformationAtom);
  const [formValues, setFormValues] = useState<Partial<EditAppointmentFormValues>>({});

  const appointmentLocation = formValues.appointmentLocation || appointmentData?.appointmentLocation;
  const startDate =
    formValues.startDate ||
    (appointmentData?.appointmentStartTime ? new Date(appointmentData.appointmentStartTime) : new Date());
  const endDate =
    formValues.endDate ||
    (appointmentData?.appointmentEndTime ? new Date(appointmentData.appointmentEndTime) : new Date());

  const values = {
    appointmentLocation,
    startDate,
    endDate,
    isRecurring: formValues.isRecurring,
    numRecurrences: formValues.numRecurrences,
    weeksBetween: formValues.weeksBetween,
  };

  const updateValues = (updates: Partial<EditAppointmentFormValues>): void => {
    setFormValues((prevValues) => ({
      ...prevValues,
      ...updates,
    }));
  };

  const timeInputErrorMessage = getTimeInputValidationMessage(values.startDate, values.endDate);

  const { data: alertsData } = useGetSchedulingAlerts(
    values.startDate,
    values.endDate,
    appointmentData?.patientId,
    values.isRecurring,
    values.numRecurrences,
    values.weeksBetween,
    appointmentData?.appointmentShortId,
    ScheduleEventType.APPOINTMENT,
  );

  if (!appointmentData) {
    return;
  }

  return (
    <EditAppointmentDrawer
      formValues={values}
      onUpdateValues={updateValues}
      timeInputErrorMessage={timeInputErrorMessage}
      alertsData={alertsData}
    />
  );
}
